import { useEffect, useMemo, useState } from "react";
import useAvailibleMenu from "./useAvailibleMenu";
import { MenuTypeConst, type MenuType, type MenuRoute, type AvailibleMenuData } from "./menuTypes";
import { FeatureKey } from "@/types/generated/FeatureKey";
import { hasFeature } from "@/hooks/useFeatures";
import { hasPermission } from "@/hooks/usePermissions";
import { UserPermission } from "@/types/generated/UserPermission";
import type { LoginInfo } from "@/types/LoginInfo";
import { translate } from "@/hooks/useLanguage";
import { LanguageKey } from "@/types/generated/LanguageKey";

/**
 * Return type of usePartnerMenu hook
 */
interface PartnerMenuData extends AvailibleMenuData {
  type: MenuType;
  loading: boolean;
  routes: MenuRoute[];
}

/**
 * Hook for managing the partner menu routes
 *
 * @param loginInfo - Current user's login information including features and permissions
 * @returns Menu data including routes, available paths, and loading state
 */
const usePartnerMenu = (loginInfo: LoginInfo): PartnerMenuData => {

  const routes = useMemo((): MenuRoute[] => {
    const { lastOrganisationFilters } = loginInfo;

    return [
      {
        collapse: true,
        name: translate(LanguageKey.trackers_and_sensors_key),
        state: "openTrackersSensors",
        views: [
          {
            view: hasPermission(UserPermission.MENU_ADMIN_TRACKERS),
            to: "/trackers_partner",
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_TRACKERS_description_key),
            subPages: [
              "/trackers_admin_update",
              "/import_tracker_settings_partner",
            ],
          },
          {
            view: hasPermission(UserPermission.MENU_ADMIN_USAGE_PROFILE),
            to: "/device_usage_profile_partner",
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_USAGE_PROFILE_description_key),
            subPages: ["/device_usage_profile_manage_partner", "/device_usage_profile_compare_partner"],
          },
          {
            view: hasPermission(UserPermission.MENU_ADMIN_BLE_SENSOR),
            to: "/ble_sensor_partner",
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_BLE_SENSOR_description_key),
            subPages: ["/ble_sensor_update", "/ble_sensor_import"],
          },
          {
            view:
              hasFeature(FeatureKey.bulk_operations) &&
              hasPermission(UserPermission.MENU_ADMIN_BULK_OPERATION),
            to: "/bulk_manage_partner",
            layout: "",
            name: translate(LanguageKey.sidebar_admin_bulk_operation_title_key),
            subPages: [
              "/bulk_operations_partner",
              "/bulk_results_detail_partner",
            ],
          },
        ],
      },
      {
        collapse: true,
        name: translate(LanguageKey.location_key),
        state: "openLocation",
        views: [
          {
            view:
              hasFeature(FeatureKey.geozone_manager) &&
              hasPermission(UserPermission.MENU_ADMIN_GEOZONES),
            to: "/areas_partner",
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_GEOZONES_description_key),
            subPages: ["/areas_update"],
          },
          {
            view:
              hasFeature(FeatureKey.loc_beacon) &&
              hasPermission(UserPermission.MENU_ADMIN_INFRASTRUCTURE),
            to: "/infrastructures_partner",
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_INFRASTRUCTURE_description_key),
            subPages: ["/infrastructures_update", "/import_infrastructure_settings_partner"],
          },
        ],
      },
      {
        collapse: true,
        name: translate(LanguageKey.organizations_key),
        state: "openOrgSetting",
        views: [
          {
            view:
              hasPermission(UserPermission.MENU_ADMIN_ORGANIZATION_LIST) &&
              hasFeature(FeatureKey.partner_org_manage) &&
              !lastOrganisationFilters,
            to: `/organization`,
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_ORGANIZATION_LIST_description_key),
          },
          {
            view:
              hasFeature(FeatureKey.demo_environment) && hasPermission(UserPermission.MENU_ADMIN_DEMO),
            to: "/org_demo",
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_DEMO_description_key),
            subPages: ["/org_demo_manage"],
          },
        ],
      },
      {
        collapse: !lastOrganisationFilters,
        name: translate(LanguageKey.access_management_key),
        state: "openParnerOrg",
        views: [
          {
            view:
              hasFeature(FeatureKey.custom_user_roles) &&
              hasPermission(UserPermission.MENU_ADMIN_USER_ROLE),
            to: "/role_manager",
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_USER_ROLE_description_key),
            subPages: ["/role_manager_update"],
          },
          {
            view:
              hasFeature(FeatureKey.organisation_visibility_filters) &&
              hasPermission(UserPermission.MENU_ADMIN_ORGANISATION_VISIBILITY_FILTER),
            to: "/manage_organisation_filters_partner",
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_ORGANISATION_VISIBILITY_FILTER_description_key),
            subPages: ["/manage_partner_organisation_filters_update"],
          },
        ],
      },
      {
        collapse: !lastOrganisationFilters,
        name: translate(LanguageKey.customization_key),
        state: "opencustomization",
        views: [
          {
            view:
              hasFeature(FeatureKey.landing_page) &&
              hasFeature(FeatureKey.partner_org_manage) &&
              hasPermission(UserPermission.MENU_ADMIN_LANDING_PAGE),
            to: `/default_landing_page_partner`,
            layout: "",
            name: translate(LanguageKey.default_landing_page_key),
          },
          {
            view:
              hasFeature(FeatureKey.report_generator) &&
              hasPermission(UserPermission.MENU_ADMIN_REPORT_TEMPLATE),
            to: "/report_template_library_partner",
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_REPORT_TEMPLATE_description_key),
            subPages: ["/report_template_library_manage_partner"],
          },
          {
            view:
              hasFeature(FeatureKey.custom_interface) &&
              hasFeature(FeatureKey.partner_org_manage) &&
              hasPermission(UserPermission.MENU_ADMIN_CUSTOM_INTERFACE),
            to: `/custom_branding_partner`,
            layout: "",
            name: translate(LanguageKey.organisation_custom_branding_key),
          },
          {
            view: hasPermission(UserPermission.MENU_ADMIN_SUPPORT),
            to: `/support_partner`,
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_SUPPORT_description_key),
          },
          {
            view:
              hasFeature(FeatureKey.dashboard) && hasPermission(UserPermission.MENU_ADMIN_DASHBOARD),
            to: "/bi_dashboards_partner",
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_DASHBOARD_description_key),
            subPages: ["/bi_dashboard_manage_partner"],
          },
          {
            view:
              hasFeature(FeatureKey.audit_log) &&
              hasPermission(UserPermission.MENU_ADMIN_AUDIT_LOG),
            to: `/audit_log_partner`,
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_AUDIT_LOG_description_key),
            subPages: ["/audit_log_view_partner", "/audit_log_aggregated_view_partner"],
          },
        ],
      },
      {
        collapse: !lastOrganisationFilters,
        name: translate(LanguageKey.ai_key),
        state: "openAi",
        views: [
          {
            view: hasFeature(FeatureKey.agents) && hasPermission(UserPermission.MENU_ADMIN_AGENT),
            to: "/enrolled_agents",
            layout: "",
            name: translate(LanguageKey.agents_section_title_key),
          },
        ],
      },
      {
        collapse: true,
        name: translate(LanguageKey.customization_key),
        state: "opencustomization",
        views: [
          {
            view:
              hasFeature(FeatureKey.image_library) &&
              hasPermission(UserPermission.MENU_ADMIN_IMAGE_LIBRARY),
            to: "/image_library_all",
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_IMAGE_LIBRARY_description_key),
            subPages: ["/image_library_manage"],
          },
        ],
      },
      {
        collapse: true,
        name: translate(LanguageKey.sidebar_support_key),
        state: "openInsights",
        views: [
          {
            view:
              hasPermission(UserPermission.MENU_ADMIN_RMA_DECLARATION) &&
              !lastOrganisationFilters,
            to: `/rma_declaration`,
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_RMA_DECLARATION_description_key),
            subPages: ["/rma_declaration_manage"],
          },
        ],
      },
      {
        collapse: true,
        name: translate(LanguageKey.platform_notifications_key),
        state: "openLocation",
        views: [
          {
            view:
              hasPermission(UserPermission.MENU_ADMIN_NOTIFICATION) &&
              !lastOrganisationFilters,
            to: "/notification",
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_NOTIFICATION_description_key),
            subPages: ["/notification_update"],
          },
        ],
      },
    ];
  }, [loginInfo]);

  const [data, setData] = useState<PartnerMenuData>({
    type: MenuTypeConst.ALL,
    loading: true,
    routes: [],
    availableMenuPaths: {},
    allPaths: {},
    defaultPath: "/organization",
  });

  const availibleMenuData = useAvailibleMenu(routes, "/organization");

  useEffect(() => {
    setData({ ...data, ...availibleMenuData, loading: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availibleMenuData]);

  return data;
};

export default usePartnerMenu;
